import {
  InventoryItem,
  AIAnalysis,
  MarketResearch,
  ItemCondition
} from '../types/inventory';
import { InventoryService } from './inventoryService';
import { supabase } from './supabaseClient';

export class ItemListingService {
  // Analyze item condition from images
  static async analyzeItemCondition(images: string[], itemName: string): Promise<{
    condition: ItemCondition;
    analysis: string;
    confidence: number;
  }> {
    const { data, error } = await supabase.functions.invoke('analyze-inventory-item', {
      body: {
        itemName,
        imageUrls: images,
        analysisType: 'condition'
      }
    });

    if (error) throw error;

    if (data?.success && data?.data) {
      const analysis = data.data;
      return {
        condition: analysis.condition as ItemCondition,
        analysis: analysis.condition_analysis,
        confidence: analysis.confidence || 85
      };
    }

    throw new Error('Failed to analyze item condition');
  }

  // Generate complete item listing
  static async generateItemListing(
    itemName: string,
    images: string[],
    userDescription?: string
  ): Promise<AIAnalysis> {
    const { data, error } = await supabase.functions.invoke('analyze-inventory-item', {
      body: {
        itemName,
        imageUrls: images,
        userDescription: userDescription || '',
        analysisType: 'full'
      }
    });

    if (error) throw error;

    if (data?.success && data?.data) {
      return data.data;
    }

    throw new Error('Failed to generate item listing');
  }

  // Create complete inventory item with AI analysis
  static async createItemWithAnalysis(
    itemName: string,
    images: File[],
    userDescription?: string
  ): Promise<InventoryItem> {
    // First create the item
    const item = await InventoryService.createItem({
      item_name: itemName,
      images: images,
      user_description: userDescription
    });

    // Generate AI analysis
    const aiAnalysis = await this.generateItemListing(
      itemName,
      item.images,
      userDescription
    );

    // Update item with AI analysis and condition
    const updatedItem = await InventoryService.updateItem(item.id, {
      item_description: aiAnalysis.description,
      condition: await this.analyzeItemCondition(item.images, itemName).then(r => r.condition),
      suggested_price: aiAnalysis.market_research.suggested_price
    });

    // Update AI analysis separately
    await InventoryService.updateAIAnalysis(item.id, aiAnalysis);

    return updatedItem;
  }

  // Research market data for an item
  static async researchMarketData(itemName: string, condition: ItemCondition): Promise<MarketResearch> {
    const { data, error } = await supabase.functions.invoke('analyze-inventory-item', {
      body: {
        itemName,
        itemCondition: condition,
        analysisType: 'market'
      }
    });

    if (error) throw error;

    if (data?.success && data?.data?.market_research) {
      return data.data.market_research;
    }

    // Fallback market research
    return {
      suggested_price: 25.00,
      price_range: { min: 15.00, max: 50.00 },
      top_marketplaces: [
        {
          marketplace: 'eBay',
          success_rate: 80,
          average_price: 25.00,
          notes: 'Popular auction site'
        }
      ],
      market_trends: ['Market data analysis not available']
    };
  }

  // Update AI analysis for existing item
  static async updateItemAnalysis(itemId: string): Promise<InventoryItem> {
    const item = await InventoryService.getItem(itemId);
    const aiAnalysis = await this.generateItemListing(
      item.item_name,
      item.images,
      item.item_description
    );

    return await InventoryService.updateAIAnalysis(itemId, aiAnalysis);
  }
}
